import { z } from 'zod';
import { discountTypeSchema } from '../const/discountTypeSchema';
import { shippingTariffSchema } from '../const/shippingTariffSchema';
import { normalizePhone } from './createOrderFromDraftSchema';

const roundTo90 = (value: number): number => {
    const safeValue = Math.max(value, 90);
    return Math.floor((safeValue - 90) / 100) * 100 + 90;
};

const itemSchema = z.object({
    productId: z.string().uuid(),
    sizeId: z.string().uuid(),
    count: z.number().min(1),
    price: z.object({
        price: z.number(),
        discount: z
            .object({
                type: z.enum(['none', 'real', 'fake']),
                percent: z.number().optional(),
            })
            .optional(),
    }),
    shoes: z.boolean(),
});

export const createPaymentSchema = z
    .object({
        tinkoffOrderId: z.string().uuid(),

        shippingTariff: shippingTariffSchema,
        city: z.string().min(1, 'This field is required'),
        address: z.string().min(3, 'This field is required'),

        fullName: z.string().min(3, 'This field is required'),
        email: z.string().min(3, { message: 'This field is required' }).email('Invalid email address'),
        // No country restriction on phone — accept any number (Georgian +995, etc.).
        phone: z
            .string()
            .optional()
            .transform(v => normalizePhone(v ?? '') ?? ''),

        discountId: z.string().uuid().optional(),
        discountType: discountTypeSchema.optional(),
        discountAmount: z.number().nonnegative().optional(),

        items: z.array(itemSchema).min(1, 'Должен быть хотя бы один товар'),
    })
    .refine(data => {
        const total = data.items.reduce<number>((acc, item) => {
            const { price, discount } = item.price;
            let final: number;

            if (!discount || discount.type === 'none' || typeof discount.percent !== 'number') {
                final = roundTo90(price);
            } else if (discount.type === 'real') {
                const pct = discount.percent;
                const raw = price * (1 - pct / 100);
                final = roundTo90(raw);
            } else {
                final = roundTo90(price);
            }

            return acc + final * item.count;
        }, 0);

        return total >= 100;
    });

export type CreatePaymentSchema = z.infer<typeof createPaymentSchema>;
